import type {Price} from '@/redux/v1/models';
import type {ExtractPropsWithoutRef} from '@/types';

import {memo, useCallback, useMemo} from 'react';

import {MapPin} from '@/components/MapPin';
import {AdvancedMarker} from '@/modules/hotels/components/MapView/components/AdvancedMarker';
import {priceFormat} from '@/utils/numberFormat';

type Props = Omit<
  ExtractPropsWithoutRef<typeof MapPin>,
  'onSelect' | 'children'
> & {
  id: string;
  price: Price;
  latitude: number;
  longitude: number;
  selected: boolean;
  isHovered?: boolean;
  splitView?: boolean;
  isPromoted?: boolean;
  onSelect: (id: string, event: google.maps.MapMouseEvent) => void;
};

export const HotelPin = memo(function HotelPin({
  id,
  price,
  latitude,
  longitude,
  selected,
  isHovered,
  onSelect,
  splitView,
  isPromoted = false,
  ...restProps
}: Props) {
  const handleClick = useCallback(
    (event: google.maps.MapMouseEvent) => {
      onSelect(id, event);
    },
    [id, onSelect]
  );

  return (
    <AdvancedMarker
      position={useMemo(
        () => ({
          lat: latitude,
          lng: longitude,
        }),
        [latitude, longitude]
      )}
      zIndex={
        selected
          ? 1 // Pins are rendered as siblings, so 1 is enough to bring to the front
          : 0
      }
      collisionBehavior={
        window.google.maps.CollisionBehavior.REQUIRED_AND_HIDES_OPTIONAL
      }
      onClick={handleClick}
    >
      <MapPin
        selected={selected}
        isHovered={isHovered}
        splitView={splitView}
        isPromoted={isPromoted}
        {...restProps}
      >
        {priceFormat(price, {
          fractionDigits: 0,
        })}
      </MapPin>
    </AdvancedMarker>
  );
});
